import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  IconButton,
  Skeleton,
  Tab,
  Tabs,
  Tooltip,
} from '@mui/material';
import { ArrowBack, Refresh } from '@mui/icons-material';
import { ACLAuthorization, ChangeRequest, Event } from '@/api/bol';
import {
  ACLAuthorizationsList,
  ChangeRequestsList,
  JsonDetailPanel,
  JsonEditor,
  LogisticsObjectCompare,
  LogisticsObjectHeader,
  PageLayout,
} from '@/components';
import { EventsTab } from '@/components/common/events';
import { useSnackbarNotification } from '@/hooks';
import { useLogisticsObject } from '@/hooks/useLogisticsObject';
import { LogisticsObjectEntity } from '@/types/logisticsObject';

interface LogisticsObjectDetailPageConfig {
  logisticsObjectType: LogisticsObjectEntity;
  routeParamKey: string;
  pageTitle: string;
  backRoute: string;
  backLabel: string;
  tabIdPrefix: string;
  tabsAriaLabel: string;
  missingIdMessage: string;
  noDataMessage: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
  tabIdPrefix: string;
}

function TabPanel({ children, value, index, tabIdPrefix, ...other }: TabPanelProps) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`${tabIdPrefix}-tabpanel-${index}`}
      aria-labelledby={`${tabIdPrefix}-tab-${index}`}
      style={{ height: '100%' }}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2, px: 0, pb: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      )}
    </div>
  );
}

function a11yProps(tabIdPrefix: string, index: number) {
  return {
    id: `${tabIdPrefix}-tab-${index}`,
    'aria-controls': `${tabIdPrefix}-tabpanel-${index}`,
  };
}

export const LogisticsObjectDetailPage: React.FC<{ config: LogisticsObjectDetailPageConfig }> = ({ config }) => {
  const params = useParams<Record<string, string>>();
  const objectId = params[config.routeParamKey] || '';
  const navigate = useNavigate();
  const { snackbarOpen, snackbarMessage, snackbarType, closeSnackbar } = useSnackbarNotification();

  const [activeTab, setActiveTab] = useState(0);
  const [selectedChangeRequest, setSelectedChangeRequest] = useState<ChangeRequest | null>(null);
  const [selectedChangeRequestId, setSelectedChangeRequestId] = useState<string | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<Event | null>(null);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const [selectedACLAuthorization, setSelectedACLAuthorization] = useState<ACLAuthorization | null>(null);
  const [selectedACLAuthorizationId, setSelectedACLAuthorizationId] = useState<string | null>(null);

  const {
    objectData,
    metadata,
    acl,
    objectLoading,
    metadataLoading,
    refetchObject,
    refetchMetadata,
    refetchChangeRequests,
    refetchACL,
  } = useLogisticsObject(config.logisticsObjectType, objectId);

  const clearSelectionState = () => {
    setSelectedChangeRequest(null);
    setSelectedChangeRequestId(null);
    setSelectedEvent(null);
    setSelectedEventId(null);
    setSelectedACLAuthorization(null);
    setSelectedACLAuthorizationId(null);
  };

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
    clearSelectionState();
  };

  const handleBack = () => {
    navigate(config.backRoute);
  };

  const handleRefreshAll = async () => {
    try {
      await Promise.all([
        refetchObject(),
        refetchMetadata(),
        refetchChangeRequests(),
        refetchACL(),
      ]);
    } catch (error) {
      console.error(`Error refreshing ${config.logisticsObjectType} data:`, error);
    }
  };

  const handleObjectUpdated = async () => {
    try {
      await Promise.all([
        refetchObject(),
        refetchMetadata(),
      ]);
    } catch (error) {
      console.error(`Error refreshing ${config.logisticsObjectType} after change request:`, error);
    }
  };

  if (!objectId) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Alert severity="error">
          {config.missingIdMessage}
        </Alert>
      </Box>
    );
  }

  return (
    <PageLayout
      title={config.pageTitle}
      selectedItemInfo={metadata ? `${objectId} • revision ${metadata.revision}` : objectId}
      leftTitleActions={
        <Tooltip title={`Refresh all ${config.logisticsObjectType} data`}>
          <IconButton
            onClick={handleRefreshAll}
            size="small"
          >
            <Refresh />
          </IconButton>
        </Tooltip>
      }
      titleActions={
        <Button
          onClick={handleBack}
          startIcon={<ArrowBack />}
          variant="contained"
          color="primary"
          size="medium"
        >
          {config.backLabel}
        </Button>
      }
      notificationOpen={snackbarOpen}
      notificationMessage={snackbarMessage}
      notificationType={snackbarType}
      onNotificationClose={closeSnackbar}
      sidePanel={(selectedChangeRequest || selectedEvent || selectedACLAuthorization) && (
        <JsonDetailPanel
          title={selectedChangeRequest ? 'Change Request Details' : selectedEvent ? 'Event Details' : 'ACL Authorization Details'}
          data={selectedChangeRequest || selectedEvent || selectedACLAuthorization}
          onClose={clearSelectionState}
        />
      )}
    >
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <LogisticsObjectHeader
          logisticsObjectType={config.logisticsObjectType}
          objectId={objectId}
          metadata={metadata}
          loading={metadataLoading}
        />

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            aria-label={config.tabsAriaLabel}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label="Details" {...a11yProps(config.tabIdPrefix, 0)} />
            <Tab label="Change Requests" {...a11yProps(config.tabIdPrefix, 1)} />
            <Tab label="Compare" {...a11yProps(config.tabIdPrefix, 2)} />
            <Tab label="Events" {...a11yProps(config.tabIdPrefix, 3)} />
            <Tab label="ACL" {...a11yProps(config.tabIdPrefix, 4)} />
          </Tabs>
        </Box>

        <Box sx={{ flex: 1, minHeight: 0 }}>
          <TabPanel value={activeTab} index={0} tabIdPrefix={config.tabIdPrefix}>
            {objectLoading && <Skeleton variant="rectangular" width="100%" sx={{ flex: 1 }} />}
            {!objectLoading && objectData && (
              <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                <JsonEditor
                  value={JSON.stringify(objectData, null, 2)}
                  onChange={() => {}}
                  height="55vh"
                  readOnly={true}
                />
              </Box>
            )}
            {!objectLoading && !objectData && (
              <Alert severity="info">
                {config.noDataMessage}
              </Alert>
            )}
          </TabPanel>

          <TabPanel value={activeTab} index={1} tabIdPrefix={config.tabIdPrefix}>
            <ChangeRequestsList
              logisticsObjectType={config.logisticsObjectType}
              objectId={objectId}
              onObjectUpdated={handleObjectUpdated}
              onChangeRequestSelected={(changeRequest) => {
                setSelectedChangeRequest(changeRequest);
                setSelectedChangeRequestId(changeRequest?.id || null);
              }}
              selectedChangeRequestId={selectedChangeRequestId}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={2} tabIdPrefix={config.tabIdPrefix}>
            <LogisticsObjectCompare
              logisticsObjectType={config.logisticsObjectType}
              objectId={objectId}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={3} tabIdPrefix={config.tabIdPrefix}>
            <EventsTab
              logisticsObjectType={config.logisticsObjectType}
              objectId={objectId}
              memberIdentifier={metadata?.memberIdentifier}
              onEventSelected={(event) => {
                setSelectedEvent(event);
                setSelectedEventId(event?.id || null);
              }}
              selectedEventId={selectedEventId}
            />
          </TabPanel>

          <TabPanel value={activeTab} index={4} tabIdPrefix={config.tabIdPrefix}>
            <ACLAuthorizationsList
              logisticsObjectType={config.logisticsObjectType}
              objectId={objectId}
              acl={acl}
              onACLUpdated={refetchACL}
              onAuthorizationSelected={(authorization) => {
                setSelectedACLAuthorization(authorization);
                setSelectedACLAuthorizationId(authorization?.memberIdentifier || null);
              }}
              selectedAuthorizationId={selectedACLAuthorizationId}
            />
          </TabPanel>
        </Box>
      </Box>
    </PageLayout>
  );
};
